import React, { useState, useEffect } from 'react';
import './Versions.scss';
import WorkplaceTable from 'components/Table/WorkplaceTable';
import BasePagination from 'base-components/BasePagination/BasePagination';
import GridColumnsConfigProvider from 'providers/GridColumnsConfigProvider';
import { GridFilterUrlProvider } from 'providers/GridFilterUrlProvider';
import BaseButton from 'base-components/BaseButton/BaseButton';
import VersionsEditModal from './parts/VersionsEditModal';
import VersionsDeleteModal from './parts/VersionDeletingModal';
import BaseValidatedDropDownList from 'base-components/BaseValidatedDropDownList/BaseValidatedDropDownList';
import BaseForm from 'base-components/BaseForm/BaseForm';
import BaseValidatedInput from 'base-components/BaseValidatedInput/BaseValidatedInput';
import { GetVersionsQueryParams, VersionItem, VersionItemAdd } from 'models/api/versions';
import { 
    getVersions, 
    createVersion, 
    updateVersion, 
    deleteVersion, 
    setVersionStatus, 
    setVersionBlock, 
    reactivateVersion,
    hardDeleteVersion
} from 'api/versionsApi';
import useApi from 'hooks/useApi';
import { VersionStatus } from 'models/api/status';
import { setEmergencyMaintenance, completeMaintenance } from 'api/maintenanceApi';
import { defaultPagination, PLATFORMS } from 'utils/consts';
import { useAuth } from 'hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { Platform } from 'models/api/platform';
import { DropDown } from 'models/api/dropDown';
import { ResponseData } from 'models/api/common';
import { generateColumns } from './columns';

const Versions: React.FC = () => {
    const api = useApi();
    const { hasEmergency, fetchGeneralInfo } = useAuth();
    const [searchParams] = useSearchParams()

    const [tableData, setTableData] = useState<ResponseData<VersionItem>>({ records: [], allRecordsCount: 0 });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState<Platform>(Platform.All);
    const [isDeleted, setIsDeleted] = useState(false);
    const [isCreateModalVisible, setCreateModalVisible] = useState(false);
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const [editableRow, setEditableRow] = useState({} as VersionItem)
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        text: string;
        handleConfirm: (() => void) | (() => Promise<void>);
    }>({
        isOpen: false,
        title: '',
        text: '',
        handleConfirm: () => ({}),
    });

    const filter = {
        orderColumn: 'status',
        orderDesc: true,
    };
    const filterParams = {
        orderColumn: 'platform',
        orderDesc: true,
    }

    const fetchVersions = async (filter?: Platform, deleted = false) => {
        setLoading(true);

        setError(null);

        const pageSize = Number(searchParams.get('pageSize'));
        const pageNumber = Number(searchParams.get('pageNum'));

        const params: GetVersionsQueryParams = pageSize || pageNumber ? { pageSize, pageNumber } : { ...defaultPagination };

        const currentPlatform = filter !== undefined ? filter : selectedPlatform;
        const requestPlatform = currentPlatform === Platform.All ? null : currentPlatform;
        params.platform = requestPlatform;

        try {
            const data = await getVersions(api, params, !deleted);
            setTableData(data);
        } catch (err: unknown) {
          setError((err as Error).message || 'Failed to load versions');
        } finally {
          setLoading(false);
        }
    };   
    const fetchData = async () => fetchVersions(selectedPlatform, isDeleted);

    const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

    const handleMaintenance = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Техническое обслуживание',
            text: hasEmergency ? 'Вы уверены, что хотите снять все версии с ТО?' : 'Вы уверены, что хотите поставить все версии на ТО?',
            handleConfirm: setMaintenance
        });
    }
    const setMaintenance = async () => {
        try {
            if (hasEmergency) {
                await completeMaintenance(api, { platform: Platform.All })
            } else {
                await setEmergencyMaintenance(api)
            }
        } catch (e) {
            console.error('Error while request: ', e)
        } finally {
            await fetchGeneralInfo()
        }
    }

    const deleteRow = async (id: string) => {
        await deleteVersion(api, id)

        await fetchData()
    }
    const handleDeleteRow = (row: VersionItem) => {
        setConfirmModal({
            isOpen: true,
            title: 'Удаление версии',
            text: 'Вы уверены, что хотите удалить версию?',
            handleConfirm: () => deleteRow(row.id)
        });
    };

    const handleRestoreVersion = async (row: VersionItem) => {
        try {
            setConfirmModal({
                isOpen: true,
                title: 'Восстановление версии',
                text: 'Вы уверены, что хотите восстановить версию?',
                handleConfirm: () => restoreRow(row.id)
            });
        } catch (error) {
            console.error('Ошибка при восстановлении версии:', error);
        }
    }
    const restoreRow = async (id: string) => {
        await reactivateVersion(api, id)

        await fetchData()
    }

    const hardDeleteRow = async (id: string) => {
        await hardDeleteVersion(api, id)

        await fetchData()
    }
    const handleHardDeleteVersion = (row: VersionItem) => {
        setConfirmModal({
            isOpen: true,
            title: 'Жесткое удаление версии',
            text: 'Вы уверены, что хотите навсегда удалить версию? Это действие нельзя отменить.',
            handleConfirm: () => hardDeleteRow(row.id)
        });
    }

    const setVersionHandler = async (form: VersionItemAdd) => {
        await createVersion(api, form);

        await fetchData()
    };

    const setEditableVersionHandler = async (form: VersionItem, shouldUpdateBody?: boolean, shouldUpdateStatus?: boolean) => {
        if (shouldUpdateBody) {
            await updateVersion(api, form);
        }

        if (shouldUpdateStatus) {
            if (form.status === VersionStatus.Blocked) {
                await setVersionBlock(api, form.id);
            } else {
                await setVersionStatus(api, form.id, form.status);
            }
        }

        await fetchData()
    }

    const handleEditModalOpening = (row: VersionItem) => {
        setEditableRow(row)

        setEditModalVisible(true);
    }

    const handleSwitchChange = async (value: boolean) => {
        setIsDeleted(value);

        await fetchVersions(selectedPlatform, value);
    }
    const handlePlatformChange = async (filter: DropDown) => {
        const platformId = filter.id as Platform;

        setSelectedPlatform(platformId);

        await fetchVersions(platformId, isDeleted);
    }

    const columns = generateColumns(
        handleEditModalOpening,
        handleDeleteRow,
        handleRestoreVersion,
        handleHardDeleteVersion,
    );

    useEffect(() => {
        fetchData()
    }, [searchParams])

    return (
        <header className="versions">
            <div className="versions__left">
                <h1>Список версий</h1>
                <div className='d-flex justify-content-between align-items-center my-4'>
                    <BaseButton
                        className="sidebar__button"
                        type="submit"
                        color="primary"
                        data-testid="login-button"
                        onClick={() => setCreateModalVisible(true)}
                    >
                        <span>
                            Добавить версию
                        </span>
                    </BaseButton>
                    {
                        hasEmergency !== null && (
                            <BaseButton
                                className="sidebar__button"
                                type="submit"
                                color={hasEmergency ? 'success' : 'danger'}
                                data-testid="login-button"
                                onClick={handleMaintenance}
                            >
                                <span>
                                    { hasEmergency ? 'Снять с ТО' : 'Поставить на ТО' }
                                </span>
                            </BaseButton>
                        )
                    }
                </div>
                <div className="w-50">
                    <BaseForm className="d-flex align-items-center gap-4">
                        <BaseValidatedDropDownList
                            label="Платформа"
                            name="platform"
                            className="base-dropdown-list__wrapper"
                            items={PLATFORMS}
                            defaultValue={Platform.All}
                            onValueChange={handlePlatformChange}
                        />

                        <BaseValidatedInput
                            id="deleted-versions"
                            type="checkbox"
                            name="isDeleted"
                            label="Удаленные версии"
                            onChange={handleSwitchChange}
                        />
                    </BaseForm>
                </div>
            </div>
            {
                error ? (<div>Error: {error}</div>) :
                (
                    <div className="versions__right">
                        <GridFilterUrlProvider filterParams={filterParams}>
                            <GridColumnsConfigProvider storagePath={'/'} initColumns={columns}>
                                <WorkplaceTable
                                    showSummaryRows
                                    lineHeight={12}
                                    rowClass={() => 'test'}
                                    clickable={false}
                                    tableData={tableData}
                                    filter={filter}
                                    tableLoading={loading}
                                >
                                    {true && (
                                        <div className="base-data-grid__footer">
                                            <BasePagination tableData={tableData} />
                                        </div>
                                    )}
                                </WorkplaceTable>
                            </GridColumnsConfigProvider>
                        </GridFilterUrlProvider>
                        <VersionsEditModal<VersionItemAdd>
                            isOpen={isCreateModalVisible} 
                            toggle={setCreateModalVisible}
                            handleChange={setVersionHandler}
                        />
                        <VersionsEditModal<VersionItem>
                            isEditing
                            isOpen={isEditModalVisible} 
                            toggle={setEditModalVisible}
                            handleChange={setEditableVersionHandler}
                            editableRow={editableRow}
                        />
                        <VersionsDeleteModal
                            isOpen={confirmModal.isOpen} 
                            toggle={closeConfirmModal}
                            handleChange={confirmModal.handleConfirm}
                            title={confirmModal.title}
                            text={confirmModal.text}
                        />       
                    </div>
                )
            }
        </header>
    );
};

export default Versions;
